import type { ContactQueryField } from "./types";

import {
  ACTIVE_WORKFLOW_OPERATORS,
  APPOINTMENT_OPERATORS,
  BOOLEAN_OPERATORS,
  BROADCAST_ACTIVITY_OPERATORS,
  BROADCAST_OPERATORS,
  BROADCAST_STATUS_OPERATORS,
  LIFECYCLE_HISTORY_OPERATORS,
  ORDER_OPERATORS,
  PAID_OPERATORS,
  SEGMENT_OPERATORS,
  SELECT_OPERATORS,
  TEXT_OPERATORS,
  TIME_OPERATORS,
  VISIT_HISTORY_OPERATORS,
} from "./query-builder.operators";

export const CONTACT_FILTER_FIELDS: ContactQueryField[] = [
  {
    name: "firstName",
    label: "First name",
    inputType: "text",
    operators: TEXT_OPERATORS,
  },
  {
    name: "lastName",
    label: "Last name",
    inputType: "text",
    operators: TEXT_OPERATORS,
  },
  {
    name: "email",
    label: "Email",
    inputType: "text",
    operators: TEXT_OPERATORS,
  },
  {
    name: "phone",
    label: "Phone",
    inputType: "text",
    operators: TEXT_OPERATORS,
  },
  {
    name: "country",
    label: "Country",
    inputType: "select",
    operators: SELECT_OPERATORS,
  },
  {
    name: "language",
    label: "Language",
    inputType: "select",
    operators: SELECT_OPERATORS,
  },
  {
    name: "assignee",
    label: "Assignee",
    inputType: "select",
    operators: SELECT_OPERATORS,
  },
  {
    name: "status",
    label: "Status",
    inputType: "select",
    operators: SELECT_OPERATORS,
  },
  {
    name: "createdAt",
    label: "Created at",
    inputType: "datetime",
    operators: TIME_OPERATORS,
  },
  {
    name: "channel",
    label: "Channel",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "lastInteractedChannel",
    label: "Last interacted channel",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "lifecycle",
    label: "Lifecycle",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "lifecycleKind",
    label: "Lifecycle type",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "lifecycleHistory",
    label: "Lifecycle history",
    inputType: "multiselect",
    operators: LIFECYCLE_HISTORY_OPERATORS,
  },
  {
    name: "activeWorkflow",
    label: "Active workflow",
    inputType: "multiselect",
    operators: ACTIVE_WORKFLOW_OPERATORS,
  },
  {
    name: "closingNote",
    label: "Last closing note",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "tags",
    label: "Tags",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "segment",
    label: "Segment",
    inputType: "multiselect",
    operators: SEGMENT_OPERATORS,
  },
  {
    name: "lastInteractedAt",
    label: "Last interacted at",
    inputType: "datetime",
    operators: TIME_OPERATORS,
  },
  {
    name: "advertisement",
    label: "Advertisement",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "adset",
    label: "Adset name",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "adSource",
    label: "Ad source",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "website",
    label: "Website",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "form",
    label: "Form",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "origin",
    label: "Origin",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "broadcast",
    label: "Broadcast",
    inputType: "multiselect",
    operators: BROADCAST_OPERATORS,
  },
  {
    name: "broadcastStatus",
    label: "Broadcast status",
    inputType: "select",
    operators: BROADCAST_STATUS_OPERATORS,
  },
  {
    name: "broadcastActivity",
    label: "Broadcast activity",
    inputType: "datetime",
    operators: BROADCAST_ACTIVITY_OPERATORS,
  },
  {
    name: "utmSource",
    label: "UTM Source",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "utmMedium",
    label: "UTM Medium",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "utmCampaign",
    label: "UTM Campaign",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "utmTerm",
    label: "UTM Term",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "utmContent",
    label: "UTM Content",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  // Clinic workspaces filter on treatments, ecommerce ones on products -
  // pickers only ever show the one that applies.
  {
    name: "treatment",
    label: "Treatments",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "product",
    label: "Products",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  // Clinic only: read through the treatments on the contact, never stored.
  {
    name: "department",
    label: "Departments",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  // Booked a treatment (clinic appointments) / placed an order (ecommerce).
  // Existence operators carry no value; date operators reuse the datetime
  // editors. The picker shows only the one matching the org type.
  {
    name: "appointment",
    label: "Appointment",
    inputType: "datetime",
    operators: APPOINTMENT_OPERATORS,
  },
  {
    name: "lastAppointmentStatus",
    label: "Last appointment status",
    inputType: "multiselect",
    operators: SELECT_OPERATORS,
  },
  {
    name: "visitHistory",
    label: "Visit history",
    inputType: "multiselect",
    operators: VISIT_HISTORY_OPERATORS,
  },
  {
    name: "paid",
    label: "Paid",
    inputType: "datetime",
    operators: PAID_OPERATORS,
  },
  {
    name: "order",
    label: "Order",
    inputType: "datetime",
    operators: ORDER_OPERATORS,
  },
  {
    name: "followUpRequired",
    label: "Has follow-up",
    inputType: "boolean",
    operators: BOOLEAN_OPERATORS,
  },
  {
    name: "followUpDue",
    label: "Follow-up due",
    inputType: "boolean",
    operators: BOOLEAN_OPERATORS,
  },
  {
    name: "needsHuman",
    label: "Needs human",
    inputType: "boolean",
    operators: BOOLEAN_OPERATORS,
  },
  {
    name: "knowledgeGap",
    label: "Knowledge gap",
    inputType: "boolean",
    operators: BOOLEAN_OPERATORS,
  },
  {
    name: "draftPending",
    label: "Draft ready",
    inputType: "boolean",
    operators: BOOLEAN_OPERATORS,
  },
  {
    name: "questionPending",
    label: "Question pending",
    inputType: "boolean",
    operators: BOOLEAN_OPERATORS,
  },
  {
    name: "approvalPending",
    label: "Waiting on approval",
    inputType: "boolean",
    operators: BOOLEAN_OPERATORS,
  },
  {
    name: "needsYou",
    label: "Needs you",
    inputType: "boolean",
    operators: BOOLEAN_OPERATORS,
  },
];
